import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2, ArrowLeft, Calendar, Clock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = "/api";
const AQUAMARINE = "#5FE3D0";
const UNAVAILABLE_GREEN = "#10B981";
const BRAND_BLUE = "#1E3A8A";

const WEEKDAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function TutorDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, getAuthHeader } = useAuth();
  const [profile, setProfile] = useState(null);
  const [slotsData, setSlotsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [trialDialog, setTrialDialog] = useState(false);
  const [trialForm, setTrialForm] = useState({
    student_name: "",
    email: "",
    whatsapp: "",
    english_level: "Beginner",
  });
  const [trialLimit, setTrialLimit] = useState(null);

  // Check trial limit when email is filled
  useEffect(() => {
    if (trialForm.email && trialForm.email.includes("@")) {
      const tm = setTimeout(() => {
        axios
          .get(`${API}/trial/trial-limit`, { params: { email: trialForm.email, student_name: trialForm.student_name } })
          .then((r) => setTrialLimit(r.data))
          .catch(() => setTrialLimit(null));
      }, 500);
      return () => clearTimeout(tm);
    }
    setTrialLimit(null);
  }, [trialForm.email, trialForm.student_name]);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/tutors/public/${id}`),
      axios.get(`${API}/tutors/public/${id}/available-slots?weeks=4`),
    ])
      .then(([p, s]) => {
        setProfile(p.data);
        setSlotsData(s.data);
      })
      .catch((e) => {
        toast.error(e?.response?.data?.detail || "Failed to load tutor");
        navigate("/tutors");
      })
      .finally(() => setLoading(false));
  }, [id, navigate]);

  // Group slots by date
  const grouped = useMemo(() => {
    const g = {};
    (slotsData?.slots || []).forEach((s) => {
      if (!g[s.date]) g[s.date] = [];
      g[s.date].push(s);
    });
    return g;
  }, [slotsData]);

  const dateList = Object.keys(grouped).sort();

  const refreshSlots = async () => {
    const r = await axios.get(`${API}/tutors/public/${id}/available-slots?weeks=4`);
    setSlotsData(r.data);
  };

  const handleBookClick = (date, time, available) => {
    if (!available) return;
    setSelectedDate(date);
    setSelectedTime(time);
  };

  const handleConfirmBook = async (isTrial) => {
    if (!selectedDate || !selectedTime) return;

    if (isTrial) {
      // Public trial: open form
      setTrialDialog(true);
      return;
    }

    // Logged-in student booking
    if (!user || user.role !== "student") {
      toast.error("Please log in as a student to book a class");
      navigate("/student-login");
      return;
    }

    setBooking(true);
    try {
      await axios.post(
        `${API}/tutors/student/book-slot`,
        {
          tutor_id: slotsData.tutor_user_id,
          date: selectedDate,
          time: selectedTime,
          is_trial: false,
        },
        { headers: getAuthHeader() }
      );
      toast.success(`Class booked for ${selectedDate} at ${selectedTime}!`);
      setSelectedDate(null);
      setSelectedTime(null);
      await refreshSlots();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Booking failed");
    } finally {
      setBooking(false);
    }
  };

  const handleSubmitTrial = async () => {
    if (!trialForm.student_name || !trialForm.email || !trialForm.whatsapp) {
      toast.error("Name, email and WhatsApp number are required");
      return;
    }
    setBooking(true);
    try {
      await axios.post(`${API}/tutors/public/book-trial`, {
        ...trialForm,
        tutor_id: slotsData.tutor_user_id,
        date: selectedDate,
        time: selectedTime,
        language: "en",
      });
      toast.success("Trial booked! Check your email for the Zoom link.");
      setTrialDialog(false);
      setSelectedDate(null);
      setSelectedTime(null);
      await refreshSlots();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Trial booking failed");
    } finally {
      setBooking(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!profile) return null;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/tutors" className="flex items-center gap-2 text-slate-700 hover:text-slate-900" data-testid="back-to-tutors">
            <ArrowLeft className="w-4 h-4" /> All Tutors
          </Link>
          <h1 className="font-bold text-lg" style={{ color: BRAND_BLUE }}>
            LanguaConnect English Class
          </h1>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Profile card */}
          <Card className="lg:col-span-1 h-fit" data-testid="tutor-profile-card">
            <CardContent className="p-6 flex flex-col items-center text-center">
              <div className="w-40 h-40 rounded-full overflow-hidden mb-4 bg-slate-100 ring-4 ring-slate-200">
                {profile.photo_url ? (
                  <img src={profile.photo_url} alt={profile.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-5xl text-slate-400">
                    {profile.name?.charAt(0)}
                  </div>
                )}
              </div>
              <h2 className="text-2xl font-bold" style={{ color: BRAND_BLUE }} data-testid="tutor-detail-name">
                {profile.name}
              </h2>
              {profile.tagline && <p className="text-sm font-medium text-slate-700 mt-1">{profile.tagline}</p>}
              {profile.specialization && (
                <p className="text-xs uppercase tracking-wider text-slate-500 mt-2">{profile.specialization}</p>
              )}
              {profile.experience_years != null && (
                <p className="text-xs text-slate-500 mt-1">{profile.experience_years} years of experience</p>
              )}
              <div className="mt-5 text-sm text-slate-700 whitespace-pre-wrap text-left">
                {profile.bio || ""}
              </div>
              {profile.is_trial_eligible && (
                <Button
                  onClick={() => {
                    // Scroll to first available slot, then auto-open trial dialog when student picks one
                    const firstAvail = document.querySelector('[data-testid^="slot-"]:not([disabled])');
                    if (firstAvail) {
                      firstAvail.scrollIntoView({ behavior: "smooth", block: "center" });
                      firstAvail.focus();
                    }
                    toast.info("Pick an available time below, then click 'Book as Free Trial'");
                  }}
                  className="mt-5 w-full text-white font-semibold py-3 rounded-lg shadow-md hover:opacity-90 transition-all"
                  style={{ background: "linear-gradient(90deg, #EC4899, #F472B6)" }}
                  data-testid="book-trial-cta-top"
                >
                  ✨ Book a Free Trial Class
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Booking grid */}
          <div className="lg:col-span-2 space-y-6">
            <div>
              <h3 className="text-xl font-bold flex items-center gap-2" style={{ color: BRAND_BLUE }}>
                <Calendar className="w-5 h-5" /> Available Schedule
              </h3>
              <p className="text-sm text-slate-500 mt-1">
                Times shown in <strong>Turkey/Istanbul</strong> timezone. Click an available time to book.
              </p>
              <div className="flex gap-4 mt-3 text-xs">
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 rounded" style={{ backgroundColor: AQUAMARINE }}></span> Available
                </span>
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 rounded" style={{ backgroundColor: UNAVAILABLE_GREEN }}></span> Unavailable
                </span>
              </div>
            </div>

            {dateList.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center text-slate-500">
                  This tutor has no upcoming availability yet.
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4 max-h-[640px] overflow-y-auto pr-2">
                {dateList.map((date) => {
                  const d = new Date(date + "T00:00:00");
                  const label = `${WEEKDAY_LABELS[(d.getDay() + 6) % 7]}, ${d.toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })}`;
                  return (
                    <Card key={date} data-testid={`date-block-${date}`}>
                      <CardContent className="p-4">
                        <div className="font-semibold text-sm text-slate-800 mb-3">{label}</div>
                        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                          {grouped[date].map((slot) => {
                            const isSelected = selectedDate === date && selectedTime === slot.time;
                            const bg = slot.available
                              ? isSelected
                                ? BRAND_BLUE
                                : AQUAMARINE
                              : UNAVAILABLE_GREEN;
                            const fg = slot.available && !isSelected ? "#0F172A" : "#FFFFFF";
                            return (
                              <button
                                key={`${date}-${slot.time}`}
                                disabled={!slot.available}
                                onClick={() => handleBookClick(date, slot.time, slot.available)}
                                style={{
                                  backgroundColor: bg,
                                  color: fg,
                                  opacity: slot.available ? 1 : 0.55,
                                  textDecoration: slot.available ? "none" : "line-through",
                                }}
                                className="text-xs font-medium py-2 px-2 rounded-md transition-all hover:opacity-90 disabled:cursor-not-allowed"
                                data-testid={`slot-${date}-${slot.time}`}
                                title={slot.available ? "Available" : "Unavailable"}
                              >
                                {slot.available ? slot.time : `${slot.time} • Unavailable`}
                              </button>
                            );
                          })}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}

            {selectedDate && selectedTime && (
              <Card className="border-2" style={{ borderColor: AQUAMARINE }} data-testid="booking-confirmation">
                <CardContent className="p-5 flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div>
                    <p className="text-sm text-slate-500">Selected slot</p>
                    <p className="font-bold text-lg flex items-center gap-2" style={{ color: BRAND_BLUE }}>
                      <Clock className="w-4 h-4" />
                      {selectedDate} • {selectedTime} (Istanbul)
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {profile.is_trial_eligible && (
                      <Button
                        variant="outline"
                        className="border-pink-400 text-pink-600 hover:bg-pink-50"
                        onClick={() => handleConfirmBook(true)}
                        disabled={booking}
                        data-testid="book-as-trial-btn"
                      >
                        Book as Free Trial
                      </Button>
                    )}
                    <Button
                      onClick={() => handleConfirmBook(false)}
                      disabled={booking}
                      style={{ backgroundColor: BRAND_BLUE, color: "white" }}
                      className="hover:opacity-90"
                      data-testid="confirm-booking-btn"
                    >
                      {booking ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        "Confirm Booking (uses 1 credit)"
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>

      {/* Trial booking dialog */}
      <Dialog open={trialDialog} onOpenChange={setTrialDialog}>
        <DialogContent data-testid="trial-dialog">
          <DialogHeader>
            <DialogTitle>Book a Free Trial Class</DialogTitle>
            <DialogDescription>
              No account needed! Fill in your details and we'll send the Zoom link to your email.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label htmlFor="trial-name">Full Name</Label>
              <Input
                id="trial-name"
                value={trialForm.student_name}
                onChange={(e) => setTrialForm((f) => ({ ...f, student_name: e.target.value }))}
                data-testid="trial-name-input"
              />
            </div>
            <div>
              <Label htmlFor="trial-email">Email</Label>
              <Input
                id="trial-email"
                type="email"
                value={trialForm.email}
                onChange={(e) => setTrialForm((f) => ({ ...f, email: e.target.value }))}
                data-testid="trial-email-input"
              />
              {trialLimit && (
                trialLimit.can_book ? (
                  <p className="text-xs text-emerald-600 mt-1" data-testid="trial-limit-info">
                    ✓ {trialLimit.remaining} of {trialLimit.max} free trials remaining
                  </p>
                ) : (
                  <div className="mt-2 p-3 rounded-md bg-rose-50 border border-rose-200 text-rose-700 text-xs" data-testid="trial-limit-exceeded">
                    <p className="font-semibold">⛔ Free trial limit reached</p>
                    <p className="mt-1">You've already used your {trialLimit.max} free trial classes. Please <a href="/subscribe" className="underline font-semibold">subscribe</a> to continue.</p>
                  </div>
                )
              )}
            </div>
            <div>
              <Label htmlFor="trial-whatsapp">WhatsApp</Label>
              <Input
                id="trial-whatsapp"
                value={trialForm.whatsapp}
                onChange={(e) => setTrialForm((f) => ({ ...f, whatsapp: e.target.value }))}
                data-testid="trial-whatsapp-input"
                required
              />
            </div>
            <div>
              <Label htmlFor="trial-level">English Level</Label>
              <select
                id="trial-level"
                value={trialForm.english_level}
                onChange={(e) => setTrialForm((f) => ({ ...f, english_level: e.target.value }))}
                className="w-full border border-slate-300 rounded-md py-2 px-3"
                data-testid="trial-level-input"
              >
                <option>Beginner</option>
                <option>Intermediate</option>
                <option>Advanced</option>
              </select>
            </div>
            <p className="text-xs text-slate-500 pt-1">
              Selected slot: <strong>{selectedDate}</strong> at <strong>{selectedTime}</strong> (Istanbul)
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTrialDialog(false)}>Cancel</Button>
            <Button
              onClick={handleSubmitTrial}
              disabled={booking || (trialLimit && !trialLimit.can_book)}
              style={{ backgroundColor: BRAND_BLUE, color: "white" }}
              className="disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="submit-trial-btn"
            >
              {booking ? <Loader2 className="w-4 h-4 animate-spin" /> : (trialLimit && !trialLimit.can_book) ? "Limit Reached" : "Book Trial"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
